import { useEffect, useRef, useState } from "react";
import type { GoonchaChallenge } from "@gooner/shared";
import { track } from "../../analytics";
import { api } from "../../lib/api";
import { requestGoonchaDeal } from "../lib/goonchaDeal";
import { GoonchaLoadError } from "./GoonchaLoadError";
import { cx } from "../ui/primitives";
import { CrownMark } from "../ui/Crown";

/**
 * GOONCHA — the robot check that labels the library, worn like the captcha it
 * parodies: a bordered checkbox row ("I'm not a robot", crown badge) that expands
 * into a 3×3 grid of looping tiles and a "Select all tiles with <X>" prompt.
 *
 * This component owns the whole exchange (deal, picks, verify, reroll) and hands its
 * parent exactly two facts: whether the challenge view is open (`onExpanded` — the
 * AuthModal hides its fields while it is) and the pass token when one is earned
 * (`onToken`). The token is the only thing that leaves; tile URLs are signed media
 * links and live in this component's state alone, never persisted anywhere.
 *
 * A failed verify deals a FRESH grid automatically — the server consumes a challenge
 * on any answer, so there is never a second guess at the same nine tiles.
 *
 * A failed DEAL (the `/gooncha/new` request itself aborted or 5xx'd) says so under the
 * row, with a Retry that asks for a new challenge. It used to fold back to the unchecked
 * box in silence, which left the submit locked with no reason on screen.
 */
export function Gooncha({
  mode,
  token,
  onToken,
  expanded,
  onExpanded,
}: {
  /** The funnel dimension — which door the check stands in front of. */
  mode: "login" | "register" | "chest";
  token: string | null;
  onToken: (token: string | null) => void;
  expanded: boolean;
  onExpanded: (open: boolean) => void;
}) {
  const [challenge, setChallenge] = useState<GoonchaChallenge | null>(null);
  const [picks, setPicks] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // The last request for a grid failed; cleared by the next one (checkbox or Retry).
  const [loadError, setLoadError] = useState(false);
  // 1-based count of grids seen since the checkbox click — the human-difficulty dial.
  const attemptRef = useRef(0);

  // The drop-off half of the funnel: the modal closing (unmounting us) mid-challenge
  // is an abandonment. Refs, because cleanup runs long after the last render.
  const openRef = useRef(false);
  openRef.current = expanded && !token;
  const promptRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (openRef.current) track("gooncha_abandoned", { prompt: promptRef.current });
    },
    [],
  );

  const deal = async (kind: "open" | "retry" | "reroll") => {
    setBusy(true);
    setFailed(kind === "retry");
    setLoadError(false);
    const got = await requestGoonchaDeal(api.gooncha.fresh);
    setBusy(false);
    if (got.kind === "pass") {
      // Degraded: the catalog could not grade a grid, the server waved us through.
      track("gooncha_degraded");
      onToken(got.token);
      onExpanded(false);
      return;
    }
    if (got.kind === "error") {
      // The deal itself failed (aborted, network, 5xx). Back to the collapsed row with
      // the reason and a Retry under it. The submit stays locked, so nothing is skipped,
      // and nothing asks again until the viewer does.
      setChallenge(null);
      setLoadError(true);
      onExpanded(false);
      return;
    }
    const fresh = got.challenge;
    attemptRef.current += 1;
    promptRef.current = fresh.prompt;
    setChallenge(fresh);
    setPicks(new Set());
    onExpanded(true);
    track("gooncha_shown", { prompt: fresh.prompt, mode });
  };

  const verify = async () => {
    if (!challenge || busy) return;
    setBusy(true);
    try {
      const pass = await api.gooncha.solve(challenge.challengeId, [...picks]);
      track("gooncha_passed", {
        prompt: challenge.prompt,
        mode,
        attempt: attemptRef.current,
      });
      onToken(pass.token);
      onExpanded(false);
      setChallenge(null);
    } catch {
      track("gooncha_failed", {
        prompt: challenge.prompt,
        mode,
        attempt: attemptRef.current,
      });
      setBusy(false);
      void deal("retry");
      return;
    }
    setBusy(false);
  };

  const toggle = (i: number) =>
    setPicks((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  if (!expanded) {
    // The reCAPTCHA homage: one bordered row, box on the left, brand on the right.
    return (
      <div>
        <div className="flex items-center justify-between rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-3 py-2.5">
          <button
            type="button"
            onClick={() => {
              if (!token && !busy) void deal("open");
            }}
            disabled={busy}
            aria-pressed={!!token}
            aria-label={token ? "Verified — not a robot" : "Verify you are not a robot"}
            className="flex items-center gap-2.5 text-left"
          >
            <span
              aria-hidden
              className={cx(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded border-2 transition-colors",
                token
                  ? "border-emerald-400 bg-emerald-400/15 text-emerald-400"
                  : "border-zinc-500 bg-[var(--color-ink)] hover:border-[var(--color-accent)]",
              )}
            >
              {busy ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-500 border-t-transparent" />
              ) : token ? (
                "✓"
              ) : null}
            </span>
            <span className="text-sm text-zinc-200">
              {token ? "Not a robot" : "I'm not a robot"}
            </span>
          </button>
          <span aria-hidden className="flex flex-col items-center gap-0.5 pl-3 text-zinc-500">
            <CrownMark size={16} />
            <span className="text-[9px] font-semibold uppercase tracking-widest">Gooncha</span>
          </span>
        </div>
        {loadError && !busy && !token ? (
          <GoonchaLoadError
            onRetry={() => {
              if (!busy) void deal("open");
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 rounded-lg bg-[var(--color-accent)]/15 px-3 py-2">
        <p className="text-xs text-[var(--color-accent-soft)]">Select all the tiles where</p>
        <p className="text-sm font-semibold leading-snug text-zinc-100">
          {challenge?.prompt ?? "…"}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {(challenge?.tiles ?? []).map((src, i) => (
          <button
            key={`${challenge?.challengeId}-${i}`}
            type="button"
            onClick={() => toggle(i)}
            aria-pressed={picks.has(i)}
            aria-label={`Tile ${i + 1}`}
            className={cx(
              "relative aspect-square overflow-hidden rounded-md bg-black transition-transform",
              picks.has(i)
                ? "ring-2 ring-inset ring-[var(--color-accent)] scale-[0.94]"
                : "hover:opacity-90",
            )}
          >
            <video
              src={src}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              // The panel tint behind the video: a cold rung (302 → master) can take a
              // beat, and a graded tile that reads as a black void is unanswerable.
              className="pointer-events-none h-full w-full bg-[var(--color-panel-2)] object-cover"
            />
            {picks.has(i) ? (
              <span
                aria-hidden
                className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-accent)] text-xs font-bold text-white"
              >
                ✓
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div
        className={cx("mt-1.5 min-h-[1rem] text-xs", failed ? "text-red-400" : "text-transparent")}
        aria-live="polite"
      >
        {failed ? "That didn't look right — here's a fresh one." : "placeholder"}
      </div>
      <div className="mt-1 flex items-center justify-between">
        <button
          type="button"
          onClick={() => {
            if (busy || !challenge) return;
            track("gooncha_rerolled", { prompt: challenge.prompt });
            void deal("reroll");
          }}
          disabled={busy}
          aria-label="New challenge"
          title="New challenge"
          className="rounded-full p-2 text-zinc-400 transition-colors hover:bg-[var(--color-panel-2)] hover:text-white disabled:opacity-50"
        >
          ↻
        </button>
        <button
          type="button"
          onClick={() => void verify()}
          disabled={busy || !challenge}
          className="rounded-lg bg-[var(--color-accent)] px-5 py-2 text-sm font-semibold text-white transition-colors hover:brightness-110 disabled:opacity-60"
        >
          {busy ? "…" : "Verify"}
        </button>
      </div>
    </div>
  );
}
